/**
 * Settings Store
 *
 * Manages user preferences that affect UI behavior.
 * Fetches settings from /api/settings endpoint.
 *
 * Usage:
 * ```typescript
 * import { useSettingsStore } from './stores/settingsStore'
 *
 * const { evaluationEnabled, loadSettings } = useSettingsStore()
 * ```
 */

import { create } from 'zustand'
import { applyTheme, type ThemePreference } from '../lib/theme'
import { settingsApi, tokenManager, extractApiError } from '../lib/api'

// ============================================================================
// Types
// ============================================================================

interface UserSettings {
  evaluationEnabled: boolean
  mode: 'auto' | 'manual'
  theme: ThemePreference
}

interface SettingsState {
  // State
  settings: UserSettings | null
  isLoading: boolean
  error: string | null
  lastFetched: number | null

  // Actions
  /**
   * Load user settings from API
   * @param forceRefresh - If true, bypass cache and fetch fresh data
   */
  loadSettings: (forceRefresh?: boolean) => Promise<void>
  updateEvaluationEnabled: (enabled: boolean) => void
  reset: () => void
}

// ============================================================================
// API
// ============================================================================

async function fetchSettings(): Promise<UserSettings> {
  if (!tokenManager.get()) {
    throw new Error('Not authenticated')
  }

  const response = await settingsApi.fetchAll()

  if (!response.success) {
    throw new Error(extractApiError(response, 'Failed to fetch settings'))
  }

  return {
    evaluationEnabled: response.data.evaluationEnabled,
    mode: response.data.mode,
    theme: response.data.theme as ThemePreference,
  }
}

// ============================================================================
// Store
// ============================================================================

const DEFAULT_SETTINGS: UserSettings = {
  evaluationEnabled: true,
  mode: 'auto',
  theme: 'dark',
}

/**
 * Zustand Settings Store
 * Manages user preferences
 */
export const useSettingsStore = create<SettingsState>((set, get) => ({
  // Initial state
  settings: null,
  isLoading: false,
  error: null,
  lastFetched: null,

  /**
   * Load user settings from API
   * Caches for 1 minute to avoid excessive API calls
   * @param forceRefresh - If true, bypass cache and fetch fresh data (useful for multi-tab sync)
   */
  loadSettings: async (forceRefresh = false) => {
    const state = get()

    // Skip if recently fetched (within 1 minute) unless forceRefresh is true
    if (!forceRefresh && state.lastFetched && Date.now() - state.lastFetched < 60 * 1000 && !state.error) {
      return
    }

    set({ isLoading: true, error: null })

    try {
      const data = await fetchSettings()

      const theme = (data.theme as ThemePreference) ?? DEFAULT_SETTINGS.theme
      applyTheme(theme)

      set({
        settings: {
          evaluationEnabled: data.evaluationEnabled ?? DEFAULT_SETTINGS.evaluationEnabled,
          mode: data.mode ?? DEFAULT_SETTINGS.mode,
          theme,
        },
        isLoading: false,
        lastFetched: Date.now(),
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load settings',
        isLoading: false,
        // Use defaults on error so UI doesn't break
        settings: DEFAULT_SETTINGS,
      })
    }
  },

  /**
   * Update evaluation enabled setting locally (for optimistic UI)
   * Called after successful save in settings page
   */
  updateEvaluationEnabled: (enabled: boolean) => {
    const state = get()
    if (state.settings) {
      set({
        settings: {
          ...state.settings,
          evaluationEnabled: enabled,
        },
      })
    }
  },

  /**
   * Reset store state (on logout)
   */
  reset: () => {
    set({
      settings: null,
      isLoading: false,
      error: null,
      lastFetched: null,
    })
  },
}))

/**
 * Hook to get just the evaluation enabled setting
 */
export function useEvaluationEnabled(): boolean {
  const settings = useSettingsStore(state => state.settings)
  // Default to true if settings haven't loaded yet
  return settings?.evaluationEnabled ?? true
}
